import { useState } from 'react';
import { ApiError, api } from '../api';
import { Banner, Field, Pill, Spinner, useAsync } from '../components/Common';
import { relativeTime } from '../format';

interface UserRow {
  id: number;
  email: string;
  name: string;
  role: 'admin' | 'editor' | 'viewer';
  team: string | null;
  active: boolean;
  must_change_password: boolean;
  director_view: boolean;
  last_login_at: string | null;
}

const DIRECTOR_HELP =
  'A simple reports-only screen: pick a report, pick a period, download it. No build or admin pages, and they can pull every report, billings included.';

const ROLE_HELP: Record<string, string> = {
  admin: 'Everything, including entry points, credentials and people.',
  editor: 'Builds requests, reports and schedules. Cannot see credentials.',
  viewer: 'Runs and exports the reports they can see. Nothing destructive.',
};

export function Users() {
  const users = useAsync(() => api.get<UserRow[]>('/api/users'));
  const [creating, setCreating] = useState(false);
  const [temporary, setTemporary] = useState<{ email: string; password: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);

  const update = async (user: UserRow, patch: Partial<UserRow> & { resetPassword?: boolean }) => {
    setBusyId(user.id);
    setError(null);
    try {
      const res = await api.put<{ temporaryPassword?: string }>(`/api/users/${user.id}`, patch);
      if (res.temporaryPassword) setTemporary({ email: user.email, password: res.temporaryPassword });
      users.reload();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusyId(null);
    }
  };

  if (users.loading && !users.data) return <Spinner label="Loading people…" />;
  if (users.error) return <Banner tone="error">{users.error}</Banner>;

  return (
    <>
      <div className="page-head">
        <div>
          <h1>People</h1>
          <p>
            Roles are deliberately coarse. The line that matters is between running a report and editing a request
            definition — a request can be pointed at a Bullhorn write tool, and the platform authenticates as a service
            account with access to the whole database.
          </p>
        </div>
        <div className="page-actions">
          <button className="primary" onClick={() => setCreating(true)}>
            Add person
          </button>
        </div>
      </div>

      {error && <Banner tone="error">{error}</Banner>}

      {temporary && (
        <Banner tone="warn" title="Temporary password — shown once">
          <div style={{ marginTop: 6 }}>
            <strong>{temporary.email}</strong>
            <div className="mono" style={{ fontSize: 14, marginTop: 4, userSelect: 'all' }}>
              {temporary.password}
            </div>
            <div style={{ fontSize: 12, marginTop: 6 }}>
              Pass this on out of band. They must change it at first sign-in.{' '}
              <button className="ghost small" onClick={() => setTemporary(null)}>
                Dismiss
              </button>
            </div>
          </div>
        </Banner>
      )}

      {creating && (
        <NewUser
          onClose={() => setCreating(false)}
          onCreated={(email, password) => {
            setCreating(false);
            setTemporary({ email, password });
            users.reload();
          }}
        />
      )}

      <div className="card">
        <div className="table-wrap">
          <table className="grid">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Role</th>
                <th>Team</th>
                <th title={DIRECTOR_HELP}>Director view</th>
                <th>Last sign-in</th>
                <th>Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {(users.data ?? []).map((user) => (
                <tr key={user.id}>
                  <td>{user.name}</td>
                  <td className="mono">{user.email}</td>
                  <td>
                    <select
                      value={user.role}
                      disabled={busyId === user.id}
                      title={ROLE_HELP[user.role]}
                      onChange={(e) => update(user, { role: e.target.value as UserRow['role'] })}
                      style={{ width: 'auto' }}
                    >
                      {(['viewer', 'editor', 'admin'] as const).map((role) => (
                        <option key={role} value={role}>
                          {role}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <input
                      value={user.team ?? ''}
                      disabled={busyId === user.id}
                      onBlur={(e) => e.target.value !== (user.team ?? '') && update(user, { team: e.target.value })}
                      onChange={() => undefined}
                      style={{ minWidth: 110 }}
                      placeholder="—"
                    />
                  </td>
                  <td>
                    <label style={{ display: 'inline-flex', gap: 6, alignItems: 'center', cursor: 'pointer' }} title={DIRECTOR_HELP}>
                      <input
                        type="checkbox"
                        checked={user.director_view}
                        disabled={busyId === user.id}
                        onChange={(e) => update(user, { director_view: e.target.checked })}
                      />
                      <span className="small">{user.director_view ? 'On' : 'Off'}</span>
                    </label>
                  </td>
                  <td>{relativeTime(user.last_login_at)}</td>
                  <td>
                    {user.active ? <Pill tone="ok">active</Pill> : <Pill tone="off">disabled</Pill>}{' '}
                    {user.must_change_password && <Pill tone="warn">must set password</Pill>}
                  </td>
                  <td className="right" style={{ whiteSpace: 'nowrap' }}>
                    <button className="small" disabled={busyId === user.id} onClick={() => update(user, { resetPassword: true })}>
                      Reset password
                    </button>{' '}
                    <button className="small" disabled={busyId === user.id} onClick={() => update(user, { active: !user.active })}>
                      {user.active ? 'Disable' : 'Enable'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="note">
          Changing a role or disabling an account ends that person’s sessions immediately. The last active administrator
          cannot be demoted or disabled.
        </p>
      </div>
    </>
  );
}

function NewUser({ onClose, onCreated }: { onClose: () => void; onCreated: (email: string, password: string) => void }) {
  const [form, setForm] = useState({ name: '', email: '', role: 'viewer', team: '', director_view: false });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await api.post<{ temporaryPassword: string }>('/api/users', form);
      onCreated(form.email, res.temporaryPassword);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card" style={{ marginBottom: 16, borderColor: 'var(--brand)' }}>
      <h3>Add a person</h3>
      {error && <Banner tone="error">{error}</Banner>}
      <div className="row">
        <Field label="Name">
          <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        </Field>
        <Field label="Work email">
          <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
        </Field>
      </div>
      <div className="row">
        <Field label="Role" help={ROLE_HELP[form.role]}>
          <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
            {(['viewer', 'editor', 'admin'] as const).map((role) => (
              <option key={role} value={role}>
                {role}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Team / desk" help="Used by report visibility rules, so a desk manager sees only their desk.">
          <input value={form.team} onChange={(e) => setForm({ ...form, team: e.target.value })} />
        </Field>
      </div>
      <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 14, cursor: 'pointer' }}>
        <input
          type="checkbox"
          checked={form.director_view}
          onChange={(e) => setForm({ ...form, director_view: e.target.checked })}
          style={{ marginTop: 4 }}
        />
        <span>
          <span style={{ fontWeight: 600, fontSize: 12.5 }}>Director view</span>
          <span className="small" style={{ display: 'block' }}>
            {DIRECTOR_HELP} Use with the viewer role for Mike and Thomas.
          </span>
        </span>
      </label>
      <div style={{ display: 'flex', gap: 8 }}>
        <button className="primary" onClick={submit} disabled={busy}>
          {busy ? 'Creating…' : 'Create'}
        </button>
        <button onClick={onClose} disabled={busy}>
          Cancel
        </button>
      </div>
      <p className="note">
        Most people never need this: anyone who clicks SCG Reporting in My SCG gets an account automatically, as a viewer.
        Add someone here ahead of time only to give them a role or director view before their first visit — use the same
        email they have in My SCG. The temporary password shown is only needed if they sign in here directly; arriving
        from My SCG retires it.
      </p>
    </div>
  );
}
